// Local rather than `@funkit/utils`, whose formatters go through
// `toLocaleString` — connect-rn takes no Intl dependency today. Grouping is
// comma-only, as the design draws it.

/**
 * `1062.11` → `1,062.11`. The fraction is passed through untouched: rounding a
 * balance would show the user a number they can't withdraw.
 */
export function groupThousands(amount: string): string {
  const [whole, ...fraction] = amount.split('.')
  const grouped = whole.replace(/\B(?=(\d{3})+(?!\d))/g, ',')
  return fraction.length > 0 ? `${grouped}.${fraction.join('.')}` : grouped
}

// Two decimals, always, as the drawer is specced: every figure in it is written
// to cents, and a provider's raw 2.4328% reads as a precision the buyer can't
// act on.
const PERCENT_DECIMALS = 2

const SMALLEST_PERCENT = (10 ** -PERCENT_DECIMALS).toFixed(PERCENT_DECIMALS)

/**
 * A rate as its percentage: a fraction is padded to two decimals (`0.042` →
 * `4.20%`), a whole percent stays bare (`0.01` → `1%`). A rate too small to
 * round to a hundredth reads `<0.01%`, since the row beside it still charges a
 * real amount.
 */
export function formatPercent(rate: number): string {
  // toFixed absorbs binary float error (0.005 * 100 = 0.5000000000000001).
  const percent = (rate * 100).toFixed(PERCENT_DECIMALS)
  if (Number(percent) === 0 && rate > 0) {
    return `<${SMALLEST_PERCENT}%`
  }
  return `${percent.replace(/\.00$/, '')}%`
}

/**
 * The fraction a token amount is shown to, matching web's `ASSET_DECIMALS` (see
 * the checkout's `InputAmount/utils.ts`) so the two flows read the same.
 */
export const ASSET_DECIMALS = 5

/**
 * `99.780000` → `99.78`, capped at {@link ASSET_DECIMALS}.
 *
 * Truncated rather than rounded: a figure the recipient won't actually get is
 * worse than one digit less of it. Trailing zeros go, so a round amount reads as
 * one.
 */
export function formatTokenAmount(
  amount: string,
  maxDecimals: number = ASSET_DECIMALS,
): string {
  const [whole, fraction = ''] = amount.split('.')
  const capped = fraction.slice(0, maxDecimals).replace(/0+$/, '')
  const grouped = groupThousands(whole)
  return capped ? `${grouped}.${capped}` : grouped
}

/** `99.94` → `$99.94`. Two decimals, as every USD figure in the design is. */
export function formatUsd(value: number): string {
  return `$${groupThousands(value.toFixed(2))}`
}

// The currencies the fiat rail quotes in today. A currency that isn't listed
// prints as `50.00 SGD`, which is better than showing it under some other
// currency's symbol.
const CURRENCY_SYMBOLS: Record<string, string> = {
  AUD: 'A$',
  CAD: 'C$',
  EUR: '€',
  GBP: '£',
  JPY: '¥',
  USD: '$',
}

function withCurrency(amount: string, currency: string): string {
  const symbol = CURRENCY_SYMBOLS[currency]
  return symbol === undefined ? `${amount} ${currency}` : `${symbol}${amount}`
}

/** `99.94, 'AUD'` → `A$99.94`. Fiat amounts are not always dollars. */
export function formatFiat(value: number, currency: string): string {
  return withCurrency(groupThousands(value.toFixed(2)), currency)
}

/**
 * `2, 'USD'` → `$2`, `2.5, 'USD'` → `$2.50`.
 *
 * For the deposit limit disclaimers only: a threshold reads the way the rail
 * states it ("the minimum for this purchase is 2 USD"), and cents the user
 * never has to think about are noise. Amounts they pay keep {@link formatFiat}.
 */
export function formatFiatWhole(value: number, currency: string): string {
  const amount = groupThousands(value.toFixed(2)).replace(/\.00$/, '')
  return withCurrency(amount, currency)
}
